import { z } from 'zod';
import {
  BRIEF_ASSET_MAX,
  CAPTION_MAX,
  LONG_TEXT_MAX,
  briefAssetSchema,
  briefDetailsSchema,
  hashtagListSchema,
  hexColorSchema,
  platformListSchema,
  scheduledAtSchema,
  taskTypeSchema,
} from './tasks.schema';

/**
 * Versions v1+ of a task: what the team hands back for the brief (version 0). A specialist's version is a draft the
 * client never sees; a lead, the account manager or an admin approves it, and only then it is published to the client
 * (Sprint 2 KB, GATE). Version 0 itself is the brief and lives in `tasks.schema.ts`.
 */

// --- What a version holds, per type -----------------------------------------------------------------------------------

/** What the team shows for a website's SSL. The database also has "Error", which the form never offers. */
export const SSL_CHOICES = ['Active', 'Pending', 'Not Started'] as const;
export const sslStatusSchema = z.enum([...SSL_CHOICES, 'Error']);

const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => text(max).optional();
const tooLong = (max: number) => `Keep this under ${max} characters`;
const caption = text(CAPTION_MAX).max(CAPTION_MAX, tooLong(CAPTION_MAX)).optional();

/** One section of the site: ticked once it is built, with a screenshot so the lead and the client can check it. */
export const deliverableSectionSchema = z.object({
  title: text(150).min(1, 'Name the section'),
  /** The kind of block: "Hero", "Grid", "CTA". */
  kind: optionalText(100),
  description: optionalText(500),
  built: z.boolean().default(false),
  image: briefAssetSchema.optional(),
});
export type DeliverableSection = z.infer<typeof deliverableSectionSchema>;

/**
 * The typed part of a version, every field optional so a draft can be saved half-way. Files (a post's slides, a reel's
 * video, a site's source zip) are the version's `assets`, not fields here.
 */
export const draftDeliverableSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('post'),
    caption,
    hashtags: hashtagListSchema,
    platforms: platformListSchema.default([]),
    audioNote: optionalText(200),
    scheduledUploadAt: scheduledAtSchema,
  }),
  z.object({
    type: z.literal('reel'),
    caption,
    hashtags: hashtagListSchema,
    platforms: platformListSchema.default([]),
    audioNote: optionalText(200),
    scheduledUploadAt: scheduledAtSchema,
    /** A Drive or Frame.io link, for a video too big to upload. */
    videoUrl: optionalText(500),
  }),
  z.object({
    type: z.literal('website'),
    liveUrl: optionalText(500),
    sslStatus: sslStatusSchema.default('Not Started'),
    adminPanelUrl: optionalText(500),
    adminUsername: optionalText(200),
    primaryColor: hexColorSchema.optional(),
    secondaryColor: hexColorSchema.optional(),
    logoNotes: optionalText(500),
    sections: z.array(deliverableSectionSchema).max(30).default([]),
  }),
  z.object({
    type: z.literal('monthly_update'),
    pageUrl: optionalText(500),
    /** What was changed, in the team's words. */
    resolvedNotes: optionalText(LONG_TEXT_MAX),
  }),
  /** A custom task is handed back as files and the note: it has no typed fields. */
  z.object({ type: z.literal('custom') }),
]);
export type DeliverableInput = z.input<typeof draftDeliverableSchema>;
export type Deliverable = z.output<typeof draftDeliverableSchema>;

/** What a version must have before it can go to QA. A draft can be saved without any of it. */
export function missingForQa(
  deliverable: Deliverable,
  { assets, notes }: { assets: { length: number }; notes: string },
): { path: string; message: string } | null {
  switch (deliverable.type) {
    case 'post':
      return assets.length === 0 ? { path: 'assets', message: 'Add at least one slide or image' } : null;
    case 'reel':
      return assets.length === 0 && !deliverable.videoUrl
        ? { path: 'assets', message: 'Upload the video, or paste a link to it' }
        : null;
    case 'website':
      return deliverable.liveUrl ? null : { path: 'deliverable.liveUrl', message: 'Add the preview or live URL' };
    case 'monthly_update':
      return deliverable.resolvedNotes ? null : { path: 'deliverable.resolvedNotes', message: 'Say what you changed' };
    case 'custom':
      return assets.length > 0 || notes
        ? null
        : { path: 'notes', message: 'Attach the files, or say what you are handing over' };
  }
}

// --- Submitting ---------------------------------------------------------------------------------------------------

/** Notes the specialist leaves for the lead; the lead's own reasons when asking for changes. */
export const REVIEW_NOTE_MIN = 10;
export const SUBMISSION_NOTES_MAX = 4000;

/**
 * Body of POST /api/tasks/{id}/submissions (a new version) and PUT .../submissions/{subId} (editing a draft).
 * `draft: true` saves it for later; `draft: false` sends it to QA. `progress` moves the task's slider with it.
 */
export const submitVersionInputSchema = z
  .object({
    draft: z.boolean().default(false),
    notes: text(SUBMISSION_NOTES_MAX).default(''),
    progress: z.number().int().min(0, 'From 0 to 100').max(100, 'From 0 to 100').optional(),
    assets: z.array(briefAssetSchema).max(BRIEF_ASSET_MAX, `Up to ${BRIEF_ASSET_MAX} files`).default([]),
    deliverable: draftDeliverableSchema,
  })
  .superRefine((value, ctx) => {
    if (value.draft) return;
    const missing = missingForQa(value.deliverable, value);
    if (missing) ctx.addIssue({ code: 'custom', path: missing.path.split('.'), message: missing.message });
  });
export type SubmitVersionInput = z.input<typeof submitVersionInputSchema>;
export type SubmitVersionParams = z.output<typeof submitVersionInputSchema>;

/** Body of POST .../submissions/{subId}/reject: the lead says what must change. Only staff see it. */
export const rejectSubmissionInputSchema = z.object({
  notes: text(2000).min(REVIEW_NOTE_MIN, `Say what should change (at least ${REVIEW_NOTE_MIN} characters)`),
});
export type RejectSubmissionInput = z.input<typeof rejectSubmissionInputSchema>;

/**
 * Body of POST .../submissions/{subId}/publish. `requiresClientApproval` is the review page's own switch: off
 * publishes straight to "Completed" instead of waiting for the client.
 */
export const publishSubmissionInputSchema = z.object({ requiresClientApproval: z.boolean().optional() });
export type PublishSubmissionInput = z.input<typeof publishSubmissionInputSchema>;

// --- Fixing a version after it is sent (Figma A05.4b hotfix, A05.4c specialist fix) -------------------------------

export const FIX_REASON_MIN = 3;

/**
 * Body of POST .../submissions/{subId}/fix: the version's typed fields as they should read, and why. A lead or admin
 * edits the version in place (the client is not asked again and is not told); a specialist's change is only proposed,
 * and waits for a lead while the client keeps seeing the version as it is.
 */
export const fixVersionInputSchema = z.object({
  reason: text(500).min(FIX_REASON_MIN, 'Say what you are fixing'),
  deliverable: draftDeliverableSchema,
});
export type FixVersionInput = z.input<typeof fixVersionInputSchema>;
export type FixVersionParams = z.output<typeof fixVersionInputSchema>;

/** Body of POST .../submissions/{subId}/fix/resolve: a lead applies a specialist's proposed fix, or declines it. */
export const resolveFixInputSchema = z
  .object({ approve: z.boolean(), notes: text(2000).optional() })
  .superRefine((value, ctx) => {
    if (!value.approve && (value.notes ?? '').length < REVIEW_NOTE_MIN) {
      ctx.addIssue({
        code: 'custom',
        path: ['notes'],
        message: `Say why (at least ${REVIEW_NOTE_MIN} characters)`,
      });
    }
  });
export type ResolveFixInput = z.input<typeof resolveFixInputSchema>;

// --- Reading versions ---------------------------------------------------------------------------------------------

export const INTERNAL_STATUSES = ['draft', 'pending_lead_review', 'changes_requested', 'approved'] as const;
export const internalStatusSchema = z.enum(INTERNAL_STATUSES);
export type InternalStatus = z.infer<typeof internalStatusSchema>;

export const CLIENT_STATUSES = ['pending_review', 'approved', 'revision_requested', 'not_required'] as const;
export const clientStatusSchema = z.enum(CLIENT_STATUSES);

const nullableText = z.string().nullable();

/** A version's typed fields as stored: every field of its type, `null`/empty where it was left blank. */
export const deliverableDetailsSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('post'),
    caption: nullableText,
    hashtags: z.array(z.string()),
    platforms: z.array(z.string()),
    audioNote: nullableText,
    scheduledUploadAt: nullableText,
  }),
  z.object({
    type: z.literal('reel'),
    caption: nullableText,
    hashtags: z.array(z.string()),
    platforms: z.array(z.string()),
    audioNote: nullableText,
    scheduledUploadAt: nullableText,
    videoUrl: nullableText,
  }),
  z.object({
    type: z.literal('website'),
    liveUrl: nullableText,
    sslStatus: sslStatusSchema,
    adminPanelUrl: nullableText,
    adminUsername: nullableText,
    primaryColor: nullableText,
    secondaryColor: nullableText,
    logoNotes: nullableText,
    sections: z.array(
      z.object({
        title: z.string(),
        kind: nullableText,
        description: nullableText,
        built: z.boolean(),
        image: briefAssetSchema.nullable(),
      }),
    ),
  }),
  z.object({ type: z.literal('monthly_update'), pageUrl: nullableText, resolvedNotes: nullableText }),
  z.object({ type: z.literal('custom') }),
]);
export type DeliverableDetails = z.infer<typeof deliverableDetailsSchema>;

export const versionSchema = z.object({
  id: z.string(),
  versionNumber: z.number().int(),
  authorId: z.string(),
  authorName: nullableText,
  internalStatus: internalStatusSchema,
  clientStatus: clientStatusSchema,
  /** The client can see it: a lead, the account manager or an admin has approved and sent it. */
  isPublished: z.boolean(),
  publishedAt: nullableText,
  publishedByName: nullableText,
  /** What the specialist wrote for the lead ("Made the price card text bigger…"). */
  notes: nullableText,
  /** Why the lead sent it back. Staff only. */
  internalReviewNotes: nullableText,
  leadReviewerName: nullableText,
  leadReviewedAt: nullableText,
  /** What the client asked for when they asked for a revision. */
  clientFeedback: nullableText,
  clientReviewedAt: nullableText,
  /** When a lead or admin last edited it in place (the "Edited" tag). */
  editedAt: nullableText,
  /** A specialist's fix waiting on a lead: what they would change and why. The version itself is untouched. */
  pendingFix: z
    .object({
      authorId: z.string(),
      authorName: nullableText,
      reason: z.string(),
      /** The names of what it changes ("caption", "publish time"). */
      fields: z.array(z.string()),
      /** The version as it would read with the fix. */
      proposed: deliverableDetailsSchema,
      createdAt: z.string(),
    })
    .nullable(),
  assets: z.array(briefAssetSchema),
  details: deliverableDetailsSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Version = z.infer<typeof versionSchema>;

/** GET /api/tasks/{id}/submissions: versions v1+, newest first. Version 0 is on the task. */
export const versionListSchema = z.object({ items: z.array(versionSchema) });
export type VersionList = z.infer<typeof versionListSchema>;

// --- The review queue (Figma A05.5) -------------------------------------------------------------------------------

/** `fixes` is a specialist's fix to something the client already has (hotfix step). */
export const REVIEW_TABS = ['waiting', 'fixes', 'sent_back'] as const;
export const reviewTabSchema = z.enum(REVIEW_TABS);
export type ReviewTab = z.infer<typeof reviewTabSchema>;

export const REVIEW_SORTS = ['client_date', 'waiting'] as const;
export const reviewSortSchema = z.enum(REVIEW_SORTS);
export const REVIEW_LIMIT = 100;

export const listReviewsQuerySchema = z.object({
  tab: reviewTabSchema.default('waiting'),
  q: z.string().trim().max(100).optional(),
  type: taskTypeSchema.optional(),
  sort: reviewSortSchema.default('client_date'),
  /** Only drafts from people filed under this pod (department). */
  pod: z.string().trim().max(100).optional(),
});
export type ListReviewsParams = z.output<typeof listReviewsQuerySchema>;

export const reviewItemSchema = z.object({
  taskId: z.string(),
  submissionId: z.string(),
  versionNumber: z.number().int(),
  title: z.string(),
  taskType: taskTypeSchema,
  businessName: nullableText,
  projectName: z.string(),
  submittedById: z.string(),
  submittedByName: nullableText,
  /** When it went to QA: "waiting" counts from here. */
  submittedAt: z.string(),
  /** When it goes out: the scheduled post time, or else the task's due date. Null when neither is set. */
  clientDate: nullableText,
  clientDateKind: z.enum(['publishes', 'due']).nullable(),
  /** "First draft", or the client revision this answers (n of the plan's maximum). */
  firstDraft: z.boolean(),
  /** Set on the "Draft fixes" tab: a fix to a version that was already sent, and what it changes. */
  fix: z.object({ fields: z.array(z.string()), clientApproved: z.boolean() }).nullable(),
  clientRevision: z.object({ number: z.number().int(), of: z.number().int() }).nullable(),
});
export type ReviewItem = z.infer<typeof reviewItemSchema>;

export const reviewQueueSchema = z.object({
  items: z.array(reviewItemSchema),
  /** The tab badges, whatever the search or filters. */
  counts: z.object({ waiting: z.number().int(), fixes: z.number().int(), sentBack: z.number().int() }),
  /** The pods the drafts come from, for the filter. */
  pods: z.array(z.string()),
});
export type ReviewQueue = z.infer<typeof reviewQueueSchema>;

// --- The client's side of a published version (Figma C05.2 / C03.3) ----------------------------------------------

/** What a client must say to ask for a revision: enough for the team to act on (Sprint 2 KB). */
export const CLIENT_REVISION_MIN = 15;
export const CLIENT_REVISION_MAX = 2000;
export const CLIENT_REVISION_AREAS_MAX = 8;

/**
 * Body of POST /api/tasks/{id}/submissions/{subId}/client-review. Approving closes the task; revising spends one of
 * the plan's change rounds. `areas` are the chips ("Image 2", "Colours") the client ticked under "What needs changing?".
 */
export const clientReviewInputSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('approve') }),
  z.object({
    action: z.literal('revise'),
    feedback: text(CLIENT_REVISION_MAX).min(
      CLIENT_REVISION_MIN,
      `Tell the team what to change (at least ${CLIENT_REVISION_MIN} characters)`,
    ),
    areas: z.array(z.string().trim().min(1).max(40)).max(CLIENT_REVISION_AREAS_MAX).default([]),
  }),
]);
export type ClientReviewInput = z.input<typeof clientReviewInputSchema>;
export type ClientReviewParams = z.output<typeof clientReviewInputSchema>;

/** The same typed fields as staff see, minus the site's logins: those go through the vault, never a task payload. */
const [post, reel, website, monthly, custom] = deliverableDetailsSchema.options;
export const clientDeliverableDetailsSchema = z.discriminatedUnion('type', [
  post,
  reel,
  website.omit({ adminPanelUrl: true, adminUsername: true }),
  monthly,
  custom,
]);
export type ClientDeliverableDetails = z.infer<typeof clientDeliverableDetailsSchema>;

/** A version the team has published. Nothing internal (QA notes, who approved it, drafts) is ever in here. */
export const clientVersionSchema = z.object({
  id: z.string(),
  versionNumber: z.number().int(),
  sentAt: z.string(),
  /** Who made it, as the client is told ("Rahul", "designer"). */
  sentBy: z.object({ name: z.string(), title: nullableText }).nullable(),
  clientStatus: clientStatusSchema,
  /** "What changed in v2": the author's own note to the client. */
  whatChanged: nullableText,
  /** What the client asked for when they asked for a revision on this version. */
  feedback: nullableText,
  reviewedAt: nullableText,
  assets: z.array(briefAssetSchema),
  details: clientDeliverableDetailsSchema,
});
export type ClientVersion = z.infer<typeof clientVersionSchema>;

/** GET /api/tasks/{id}/review: everything the client's review page draws, for one of their tasks. */
export const clientReviewSchema = z.object({
  task: z.object({
    id: z.string(),
    title: z.string(),
    taskType: taskTypeSchema,
    projectId: z.string(),
    /** The business's name, for the "post as" handle of the preview. */
    businessName: nullableText,
    /** The raw status; the page maps it to the client's own words. */
    status: z.string(),
    projectName: z.string(),
    deadline: nullableText,
    revisionCount: z.number().int(),
    maxRevisions: z.number().int(),
    /** How far along the team says it is (0-100): "About 60% done" while nothing is ready to look at yet. */
    progress: z.number().int(),
    /** Who is making it, as the client is told ("Aisha", "designer"): the first specialist on the task. Null while nobody is. */
    worker: z.object({ name: z.string(), title: nullableText }).nullable(),
  }),
  /**
   * The client's own brief (version 0): theirs to read, whoever wrote it. `byClient` says whether they wrote it or the
   * team did on their behalf.
   */
  brief: z
    .object({
      createdAt: z.string(),
      byClient: z.boolean(),
      assets: z.array(briefAssetSchema),
      details: briefDetailsSchema,
    })
    .nullable(),
  /** Published versions, newest first. Empty while the team is still working on the first. */
  versions: z.array(clientVersionSchema),
  /** The one the client can answer now, or null when nothing waits on them. */
  answerableId: nullableText,
});
export type ClientReview = z.infer<typeof clientReviewSchema>;

/** Why a client's revision was refused: every change round on their plan is spent. */
export const REVISIONS_USED_CODE = 'REVISIONS_USED';
